// hooks/useSignOut.js

import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { logout } from "../redux/actions/userActions";

/**
 * Custom hook to handle user sign-out logic in a reusable way.
 * Dispatches LOGOUT action which resets all Redux state,
 * sets a session flag to prevent unwanted side effects,
 * and signs out via Clerk.
 *
 * @returns {function} signOutUser - async function to perform sign-out with optional redirect
 */
export const useSignOut = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { signOut, isSignedIn } = useClerk();

  /**
   * Signs out the current user.
   *
   * @param {string} redirectUrl - URL to redirect to after sign out (default is "/")
   */
  const signOutUser = async (redirectUrl = "/") => {
    // Set flag in sessionStorage to indicate that sign out is in progress
    sessionStorage.setItem("signingOut", "true");

    // Clear any stored redirect URL from protected routes
    sessionStorage.removeItem("redirectAfterSignIn");

    // Dispatch LOGOUT to reset entire Redux state
    dispatch(logout());

    try {
      if (isSignedIn) {
        await signOut();
      }
    } catch (e) {
      // swallow Clerk errors if already signed out
    }

    navigate(redirectUrl, { replace: true });
  };

  return signOutUser;
};
